import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Download, Archive } from "lucide-react";

export default function Compliance() {
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [bundling, setBundling] = useState(false);
  const [err, setErr] = useState("");

  function load() {
    api.get("/admin/reports/ira")
       .then((r) => setReport(r.data))
       .catch((e) => setErr(e.response?.data?.detail || "Forbidden"));
  }
  useEffect(() => { load(); }, []);

  function downloadJson() {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ira-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadBundle() {
    setBundling(true);
    try {
      const { data: blob } = await api.get(
        "/admin/reports/quarterly-bundle.zip",
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nyoksure_compliance_bundle_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } finally {
      setBundling(false);
    }
  }

  return (
    <div className="p-8 space-y-6" data-testid="compliance-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{t("admin_pages.compliance_section")}</div>
          <h1 className="font-heading text-3xl font-bold mt-1">{t("admin_pages.compliance_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("admin_pages.compliance_subtitle")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="export-ira-btn" onClick={downloadJson} disabled={!report}>
            <Download className="w-4 h-4 mr-2" />{t("admin_pages.compliance_export")}
          </Button>
          <Button data-testid="export-bundle-btn" onClick={downloadBundle} disabled={bundling}
                  className="bg-[#c15337] hover:bg-[var(--terracotta-hover)] text-white">
            <Archive className="w-4 h-4 mr-2" />
            {bundling ? "Bundling…" : "Quarterly bundle (ZIP)"}
          </Button>
        </div>
      </header>

      {err && (
        <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded-sm p-3">{String(err)}</div>
      )}

      {report && (
        <div className="bg-white border border-border rounded-md p-6 space-y-4">
          <div className="text-xs text-muted-foreground font-mono">
            {t("admin_pages.compliance_generated")}: {new Date(report.generated_at).toLocaleString()}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(report.metrics).map(([k, v]) => (
              <div key={k} className="border border-border rounded-sm p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="mt-2 text-xl font-bold font-mono tracking-tight">
                  {typeof v === "number" && k.includes("kes")
                    ? `KES ${v.toLocaleString()}`
                    : typeof v === "number" && k.includes("ratio")
                    ? `${(v * 100).toFixed(2)}%`
                    : v.toLocaleString?.() ?? String(v)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
